import type { ComponentType } from 'react'
import type { TextInputProps } from 'react-native'
import { Pressable, Text, TextInput, View } from 'react-native'
import type { LucideProps } from 'lucide-react-native'
import { colors } from '@chillout/core'

type FieldSlot = ComponentType<LucideProps> | string

interface TextFieldProps extends Omit<TextInputProps, 'onPress'> {
  label?: string
  error?: string
  helperText?: string
  className?: string
  prefix?: FieldSlot
  suffix?: FieldSlot
  onSuffixPress?: () => void
  suffixAccessibilityLabel?: string
  HelperTextIcon?: ComponentType<LucideProps>
  onPress?: () => void
  required?: boolean
}

export function TextField({
  label,
  error,
  helperText,
  className,
  prefix,
  suffix,
  onSuffixPress,
  suffixAccessibilityLabel,
  HelperTextIcon,
  accessibilityLabel,
  onPress,
  required,
  multiline,
  maxLength,
  value,
  editable,
  ...inputProps
}: TextFieldProps) {
  const message = error ?? helperText
  const prefixIsString = typeof prefix === 'string'
  const suffixIsString = typeof suffix === 'string'
  const PrefixIcon = !prefixIsString ? prefix as ComponentType<LucideProps> | undefined : undefined
  const SuffixIcon = !suffixIsString ? suffix as ComponentType<LucideProps> | undefined : undefined
  const fieldTextClassName = `h-full min-w-0 flex-1 text-base leading-tight font-normal text-primary outline-none ${
    PrefixIcon ? 'pl-2' : prefixIsString ? '' : suffixIsString ? 'pl-4' : ''
  } ${
    SuffixIcon ? 'pr-2' : suffixIsString ? '' : prefixIsString ? 'pr-4' : ''
  }`

  // Container keeps px-4 unless a string prefix/suffix needs to be flush with the edge
  const containerPadding = prefixIsString || suffixIsString ? '' : 'px-4'

  const charCount = maxLength !== undefined
    ? `${(value as string)?.length ?? 0} / ${maxLength}`
    : undefined

  const inner = (
    <>
      {label ? (
        <Text className="mb-2 text-sm font-semibold text-primary">
          {label}
          {required ? <Text className="text-accent"> *</Text> : null}
        </Text>
      ) : null}
      <View
        className={`${multiline ? 'min-h-[80px] web:lg:min-h-[72px] items-start py-3' : 'h-14 web:lg:h-10 items-center'} flex-row rounded-md border bg-canvas-card ${containerPadding} ${
          error ? 'border-accent' : 'border-border'
        }`}
      >
        {prefixIsString ? (
          <View className="h-full justify-center border-r border-border bg-canvas-primary px-3 rounded-l-md">
            <Text className="text-base text-secondary">{prefix}</Text>
          </View>
        ) : PrefixIcon ? (
          <PrefixIcon size={22} color={colors.text.secondary} strokeWidth={1.8} />
        ) : null}

        {editable === false ? (
          // TextInput's numberOfLines only takes effect when multiline is
          // true (React Native's own doc comment on the prop says so) — on
          // a single-line input it's a no-op on both platforms, so a long
          // value (e.g. an email) wraps instead of staying on one line. A
          // non-editable field is display-only anyway, so render it as Text
          // instead, which properly supports numberOfLines/truncation
          // everywhere. Bonus: this also removes the need for the
          // pointerEvents workaround below for onPress-triggered fields
          // (e.g. the birthday picker trigger), since Text never intercepts
          // touches the way TextInput can on iOS.
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            accessibilityLabel={accessibilityLabel ?? label}
            // No h-full here (unlike the TextInput branch) — Text isn't a
            // flex container, so forcing its box to the row's full height
            // pushes the actual text content to the top instead of letting
            // the parent row's items-center vertically center it naturally.
            className={fieldTextClassName.replace('h-full', '')}
          >
            {value}
          </Text>
        ) : (
          <TextInput
            {...inputProps}
            value={value}
            maxLength={maxLength}
            accessibilityLabel={accessibilityLabel ?? label}
            placeholderTextColor={colors.text.muted}
            className={fieldTextClassName}
            multiline={multiline}
            style={{ textAlignVertical: multiline ? 'top' : 'center', paddingVertical: 0 }}
          />
        )}

        {suffixIsString ? (
          <View className="h-full justify-center border-l border-border bg-canvas-primary px-3 rounded-r-md">
            <Text className="text-base text-secondary">{suffix}</Text>
          </View>
        ) : SuffixIcon && onSuffixPress ? (
          <Pressable
            onPress={onSuffixPress}
            accessibilityLabel={suffixAccessibilityLabel}
            accessibilityRole="button"
            className="pl-2 items-center justify-center"
          >
            <SuffixIcon size={22} color={colors.text.secondary} strokeWidth={1.8} />
          </Pressable>
        ) : SuffixIcon ? (
          <View className="pl-2">
            <SuffixIcon size={22} color={colors.text.secondary} strokeWidth={1.8} />
          </View>
        ) : null}
      </View>
      {(message || charCount) ? (
        <View className="mt-1 flex-row items-start justify-between gap-2">
          <View className="flex-1 flex-row items-center">
            {HelperTextIcon && message ? (
              <View className="mr-1 w-8 h-8 items-center justify-center rounded-full bg-accent-surface">
                <HelperTextIcon
                  size={16}
                  color={error ? colors.accent.DEFAULT : colors.text.secondary}
                  strokeWidth={2}
                />
              </View>
            ) : null}
            {message ? (
              <Text className={`text-xs font-normal ${error ? 'text-accent' : 'text-secondary'}`}>
                {message}
              </Text>
            ) : null}
          </View>
          {charCount ? (
            <Text className="text-xs text-secondary">{charCount}</Text>
          ) : null}
        </View>
      ) : null}
    </>
  )

  if (onPress) {
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        className={`w-full ${className ?? ''}`}
      >
        {inner}
      </Pressable>
    )
  }

  return (
    <View className={`w-full ${className ?? ''}`}>
      {inner}
    </View>
  )
}
